// ROUTE-RENDER-MODEL (2026-07-22) — Fonte ÚNICA de verdade para a lógica de
// RENDERIZAÇÃO de rotas partilhada entre os vários mapas (só-leitura):
//   • TripOverviewMap  (mapa do passeio público / agregado)
//   • RouteMapViewer   (mapa "MAPA DA ETAPA" na edição do passeio)
//   • PublicRoutePage  (ficha pública / preview da rota)
//
// Antes, cada renderer tinha a SUA cópia destas regras (prioridade de fonte,
// deteção de rota circular, mapeamento dos waypoints nativos, colocação dos
// marcadores início/fim). Isso causou bugs "corrigidos aqui mas não ali"
// (ex.: início/fim a cair nos centroides dos municípios em vez dos extremos
// reais da geometria). Centralizar aqui elimina essa classe de bugs.

import { haversineKm } from "@/lib/routes-tools/exporters";

export const ROUTE_CIRCULAR_THRESHOLD_KM = 0.15;

// coords: array de [lat, lng]. Circular quando início ≈ fim.
export function isCircularRoute(coords, thresholdKm = ROUTE_CIRCULAR_THRESHOLD_KM) {
  if (!Array.isArray(coords) || coords.length < 3) return false;
  const a = coords[0];
  const b = coords[coords.length - 1];
  if (!a || !b) return false;
  return haversineKm({ lat: a[0], lng: a[1] }, { lat: b[0], lng: b[1] }) < thresholdKm;
}

// Normaliza `route_geometry.waypoints` (nativos do Editor, com kind/role) em
// paragens para o mapa. Pontos de estrada (`kind:"road"`) → `via:true` (não
// são desenhados como paragens; servem só de shaping).
export function nativeStopsFromGeometry(routeGeometry) {
  const wps = routeGeometry && Array.isArray(routeGeometry.waypoints) ? routeGeometry.waypoints : [];
  return wps
    .filter((w) => w && w.lat != null && w.lng != null
      && Number.isFinite(Number(w.lat)) && Number.isFinite(Number(w.lng)))
    .map((w) => ({
      lat: Number(w.lat),
      lng: Number(w.lng),
      name: w.name || null,
      role: w.role || null,
      category: w.category || null,
      via: w.kind === "road",
    }));
}

// Prioridade CANÓNICA da fonte de geometria para exibição (só-leitura):
//   gpx  >  google  >  precomputed (route_geometry.coords)  >  localities  >  pois
// Devolve: "gpx" | "google" | "precomputed" | "localities" | "pois_only" | null
export function pickRouteSource({
  hasGpx = false,
  hasGmaps = false,
  precomputedCoordsLen = 0,
  localitiesLen = 0,
  poisLen = 0,
} = {}) {
  if (hasGpx) return "gpx";
  if (hasGmaps) return "google";
  if (precomputedCoordsLen > 1) return "precomputed";
  if (localitiesLen > 0) return "localities";
  if (poisLen > 0) return "pois_only";
  return null;
}

// Início/fim SEMPRE a partir dos EXTREMOS REAIS da geometria (coords[0] /
// coords[último]) — nunca de centroides de localidades. `coords` em [lat,lng].
export function startEndFromCoords(coords, { startName = null, endName = null } = {}) {
  if (!Array.isArray(coords) || coords.length === 0) {
    return { start: null, end: null, isCircular: false };
  }
  const s = coords[0];
  const e = coords[coords.length - 1];
  return {
    start: { lat: s[0], lng: s[1], name: startName },
    end: { lat: e[0], lng: e[1], name: endName },
    isCircular: isCircularRoute(coords),
  };
}
